/**
 * Unified DateRange Configuration Component
 */

export type DateRangeType = "today" | "single" | "range" | "week" | "month" | "year";

export interface DateRangeParams {
  date?: string;
  start_date?: string;
  end_date?: string;
  week_value?: string;
  month_value?: string;
  year_value?: number;
}

interface DateRangeConfigProps {
  type: DateRangeType;
  params: DateRangeParams;
  onChange: (type: DateRangeType, params: DateRangeParams) => void;
  className?: string;
}

const DATE_RANGE_OPTIONS = [
  { value: "today", label: "今天", description: "处理今日数据" },
  { value: "single", label: "指定日期", description: "处理特定日期" },
  { value: "range", label: "日期区间", description: "处理日期范围内的数据" },
  { value: "week", label: "整周", description: "处理整周数据" },
  { value: "month", label: "整月", description: "处理整月数据" },
  { value: "year", label: "整年", description: "处理整年数据" },
] as const;

export function DateRangeConfig({ type, params, onChange, className = "" }: DateRangeConfigProps) {
  const handleTypeChange = (newType: DateRangeType) => {
    const today = new Date().toISOString().split("T")[0];
    const defaultParams: Record<DateRangeType, DateRangeParams> = {
      today: {},
      single: { date: today },
      range: { start_date: today, end_date: today },
      week: { week_value: getWeekValue(new Date()) },
      month: { month_value: today.slice(0, 7) },
      year: { year_value: new Date().getFullYear() },
    };
    onChange(newType, defaultParams[newType]);
  };

  const handleParamChange = (key: keyof DateRangeParams, value: unknown) => {
    onChange(type, { ...params, [key]: value });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* DateRange Type Selection */}
      <div>
        <label className="text-[#64748b] text-[10px] block mb-2">日期范围</label>
        <select
          value={type}
          onChange={(e) => handleTypeChange(e.target.value as DateRangeType)}
          className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label} - {opt.description}</option>
          ))}
        </select>
      </div>

      {/* Dynamic Parameter Input */}
      <div className="bg-[#1a1d24] border border-[var(--border)] rounded p-3">
        {type === "single" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">日期</label>
            <input
              type="date"
              value={params.date || ""}
              onChange={(e) => handleParamChange("date", e.target.value)}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {type === "range" && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">开始日期</label>
              <input
                type="date"
                value={params.start_date || ""}
                onChange={(e) => handleParamChange("start_date", e.target.value)}
                className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              />
            </div>
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">结束日期</label>
              <input
                type="date"
                value={params.end_date || ""}
                onChange={(e) => handleParamChange("end_date", e.target.value)}
                className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              />
            </div>
          </div>
        )}

        {type === "week" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">周</label>
            <input
              type="week"
              value={params.week_value || ""}
              onChange={(e) => handleParamChange("week_value", e.target.value)}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {type === "month" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">月份</label>
            <input
              type="month"
              value={params.month_value || ""}
              onChange={(e) => handleParamChange("month_value", e.target.value)}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {type === "year" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">年份</label>
            <input
              type="number"
              min={1990}
              max={2099}
              value={params.year_value || new Date().getFullYear()}
              onChange={(e) => handleParamChange("year_value", Number(e.target.value))}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {type === "today" && (
          <p className="text-[#64748b] text-[10px]">将处理今天的数据</p>
        )}
      </div>
    </div>
  );
}

function getWeekValue(date: Date): string {
  const year = date.getFullYear();
  const start = new Date(year, 0, 1);
  const days = Math.floor((date.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  const week = Math.ceil((days + start.getDay() + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
}
